// /app/frontend/src/components/admin/HandshakeSettings.js
// Configurações do sistema de Handshake Digital
import React, { useState, useEffect } from 'react';
import { 
  FiShield, FiMapPin, FiClock, FiCamera, FiKey, FiSave,
  FiRefreshCw, FiCheck
} from 'react-icons/fi';
import api from '@/services/api';

const HandshakeSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Form states
  const [pickupRadius, setPickupRadius] = useState(20);
  const [dropoffRadius, setDropoffRadius] = useState(20);
  const [qrExpiry, setQrExpiry] = useState(5);
  const [requirePhoto, setRequirePhoto] = useState(true);
  const [allowPinBackup, setAllowPinBackup] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/handshake/settings');
      setSettings(response.data);
      setPickupRadius(response.data.pickup_radius_meters);
      setDropoffRadius(response.data.dropoff_radius_meters);
      setQrExpiry(response.data.qr_expiry_minutes);
      setRequirePhoto(response.data.require_photo);
      setAllowPinBackup(response.data.allow_pin_backup);
    } catch (err) {
      setError('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put(`/handshake/settings?pickup_radius_meters=${pickupRadius}&dropoff_radius_meters=${dropoffRadius}&qr_expiry_minutes=${qrExpiry}&require_photo=${requirePhoto}&allow_pin_backup=${allowPinBackup}`);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-red-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="handshake-settings">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <FiShield className="text-green-500" />
            Handshake Digital - Configurações
          </h3>
          <p className="text-sm text-gray-500">
            Configure os parâmetros de segurança para coleta e entrega
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
        >
          {saving ? (
            <FiRefreshCw className="animate-spin" />
          ) : saved ? (
            <FiCheck />
          ) : (
            <FiSave />
          )}
          {saving ? 'Salvando...' : saved ? 'Salvo!' : 'Salvar'}
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Explicação */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h4 className="font-medium text-blue-800 mb-2">Como funciona o Handshake Digital:</h4>
        <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
          <li>Entregador chega na loja e escaneia o QR fixo da loja</li>
          <li>Lojista mostra o QR do pedido, entregador escaneia</li>
          <li>Pedido muda para "Em Rota"</li>
          <li>Entregador chega no cliente (validação GPS)</li>
          <li>Entregador escaneia QR do cliente</li>
          <li>Cliente escaneia QR do produto</li>
          <li>Entregador tira foto do pacote no endereço</li>
          <li>Pagamento é liberado</li>
        </ol>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Raio de Coleta */}
        <div className="bg-white p-4 rounded-xl border">
          <label className="flex items-center gap-2 font-medium text-gray-800 mb-2">
            <FiMapPin className="text-orange-500" />
            Raio de Coleta (metros)
          </label>
          <p className="text-sm text-gray-500 mb-3">
            Distância máxima do entregador à loja para iniciar coleta
          </p>
          <input
            type="range"
            min="10"
            max="100"
            step="5"
            value={pickupRadius}
            onChange={(e) => setPickupRadius(parseInt(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-sm text-gray-500 mt-1">
            <span>10m</span>
            <span className="font-bold text-lg text-orange-600">{pickupRadius}m</span>
            <span>100m</span>
          </div>
        </div>

        {/* Raio de Entrega */}
        <div className="bg-white p-4 rounded-xl border">
          <label className="flex items-center gap-2 font-medium text-gray-800 mb-2">
            <FiMapPin className="text-green-500" />
            Raio de Entrega (metros)
          </label>
          <p className="text-sm text-gray-500 mb-3">
            Distância máxima do entregador ao cliente para confirmar chegada
          </p>
          <input
            type="range"
            min="10"
            max="100"
            step="5"
            value={dropoffRadius}
            onChange={(e) => setDropoffRadius(parseInt(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-sm text-gray-500 mt-1">
            <span>10m</span>
            <span className="font-bold text-lg text-green-600">{dropoffRadius}m</span>
            <span>100m</span>
          </div>
        </div>

        {/* Validade do QR */}
        <div className="bg-white p-4 rounded-xl border">
          <label className="flex items-center gap-2 font-medium text-gray-800 mb-2">
            <FiClock className="text-blue-500" />
            Validade do QR Code (minutos)
          </label>
          <p className="text-sm text-gray-500 mb-3">
            Tempo de expiração dos QR codes de segurança
          </p>
          <select
            value={qrExpiry}
            onChange={(e) => setQrExpiry(parseInt(e.target.value))}
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value={3}>3 minutos</option>
            <option value={5}>5 minutos</option>
            <option value={10}>10 minutos</option>
            <option value={15}>15 minutos</option>
            <option value={30}>30 minutos</option>
          </select>
        </div>

        {/* Foto Obrigatória */}
        <div className="bg-white p-4 rounded-xl border">
          <label className="flex items-center gap-2 font-medium text-gray-800 mb-2">
            <FiCamera className="text-purple-500" />
            Foto de Entrega
          </label>
          <p className="text-sm text-gray-500 mb-3">
            Exigir foto do pacote junto ao número da casa
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setRequirePhoto(true)}
              className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                requirePhoto
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Obrigatória
            </button>
            <button
              onClick={() => setRequirePhoto(false)}
              className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                !requirePhoto
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Opcional
            </button>
          </div>
        </div>

        {/* PIN de Backup */}
        <div className="bg-white p-4 rounded-xl border md:col-span-2">
          <label className="flex items-center gap-2 font-medium text-gray-800 mb-2">
            <FiKey className="text-yellow-500" />
            PIN de Backup
          </label>
          <p className="text-sm text-gray-500 mb-3">
            Permitir código PIN como alternativa ao QR Code (para clientes idosos ou câmera com defeito)
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setAllowPinBackup(true)}
              className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                allowPinBackup
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Permitir PIN
            </button>
            <button
              onClick={() => setAllowPinBackup(false)}
              className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                !allowPinBackup
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Apenas QR Code
            </button>
          </div>
        </div>
      </div>

      {/* Resumo */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h4 className="font-medium text-gray-800 mb-3">Configuração Atual:</h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-orange-600">{pickupRadius}m</p>
            <p className="text-xs text-gray-500">Raio Coleta</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-600">{dropoffRadius}m</p>
            <p className="text-xs text-gray-500">Raio Entrega</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-blue-600">{qrExpiry}min</p>
            <p className="text-xs text-gray-500">Validade QR</p>
          </div>
          <div>
            <p className="text-2xl font-bold" style={{ color: requirePhoto ? '#16a34a' : '#ca8a04' }}>
              {requirePhoto ? 'Sim' : 'Não'}
            </p>
            <p className="text-xs text-gray-500">Foto Obrig.</p>
          </div>
          <div>
            <p className="text-2xl font-bold" style={{ color: allowPinBackup ? '#16a34a' : '#dc2626' }}>
              {allowPinBackup ? 'Sim' : 'Não'}
            </p>
            <p className="text-xs text-gray-500">PIN Backup</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HandshakeSettings;
